// Client-side event store using localStorage for persistence
// This survives serverless cold starts because data lives in the browser

export interface PageViewEvent {
  id: string;
  type: 'pageview';
  timestamp: string;
  page: string;
  userAgent: string;
}

export interface ClickEvent {
  id: string;
  type: 'click';
  timestamp: string;
  target: string;
  userAgent: string;
}

export interface DownloadEvent {
  id: string;
  type: 'download';
  timestamp: string;
  url: string;
  userAgent: string;
}

export type TrackEvent = PageViewEvent | ClickEvent | DownloadEvent;

const STORAGE_KEY = 'nightplay_events';
const MAX_EVENTS = 2000;

function loadEvents(): TrackEvent[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveEvents(events: TrackEvent[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    // localStorage full — trim and retry
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(0, 500)));
    } catch {
      // ignore
    }
  }
}

export function pushEvent(event: Omit<TrackEvent, 'id' | 'timestamp'>): void {
  const events = loadEvents();
  const newEvent = {
    ...event,
    id: `${event.type}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    timestamp: new Date().toISOString(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  } as TrackEvent;
  events.unshift(newEvent);
  if (events.length > MAX_EVENTS) events.splice(MAX_EVENTS);
  saveEvents(events);
  // Dispatch a custom event so admin page can react immediately
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('nightplay_event', { detail: newEvent }));
  }
}

export function getEvents(): TrackEvent[] {
  return loadEvents();
}

export function getStats() {
  const events = loadEvents();
  const now = Date.now();
  const last24h = now - 86400000;

  const pageViews = events.filter((e) => e.type === 'pageview') as PageViewEvent[];
  const clicks = events.filter((e) => e.type === 'click') as ClickEvent[];
  const downloads = events.filter((e) => e.type === 'download') as DownloadEvent[];

  return {
    totalPageViews: pageViews.length,
    totalClicks: clicks.length,
    totalDownloads: downloads.length,
    pageViewsLast24h: pageViews.filter((e) => new Date(e.timestamp).getTime() > last24h).length,
    clicksLast24h: clicks.filter((e) => new Date(e.timestamp).getTime() > last24h).length,
    downloadsLast24h: downloads.filter((e) => new Date(e.timestamp).getTime() > last24h).length,
    recentPageViews: pageViews.slice(0, 20),
    recentClicks: clicks.slice(0, 20),
    recentDownloads: downloads.slice(0, 20),
  };
}
